import { TZDate } from '@date-fns/tz';
import { format, isValid, parse, parseISO } from 'date-fns';

type MonthFormat = '2-digit' | 'long' | 'narrow' | 'numeric' | 'short';

interface DateOptions {
  monthFormat?: MonthFormat;
  timezone?: string;
}

const UNKNOWN_DATE = 'Unknown Date';
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const SECONDS_THRESHOLD = 10_000_000_000;

const UTC_TIMESTAMP_FORMAT = "yyyy-MM-dd, HH:mm 'UTC'";

const MONTH_FORMAT_PATTERNS = {
  '2-digit': 'MM d, yyyy',
  long: 'MMMM d, yyyy',
  narrow: 'MMMMM d, yyyy',
  numeric: 'M d, yyyy',
  short: 'MMM d, yyyy',
} as const satisfies Record<MonthFormat, string>;

function isKnownDate(date: Date): boolean {
  return isValid(date) && !Number.isNaN(date.getTime());
}

function isKnownTimestamp(timestamp: number): boolean {
  return timestamp !== 0 && Number.isFinite(timestamp);
}

function toUnixMilliseconds(input: number): number {
  return input < SECONDS_THRESHOLD ? input * 1000 : input;
}

function formatKnownDate(date: Date, pattern: string): string {
  if (!isKnownDate(date)) {
    return UNKNOWN_DATE;
  }

  try {
    return format(date, pattern);
  } catch {
    return UNKNOWN_DATE;
  }
}

function parseFlexibleDateInput(input: number | string): Date | null {
  if (input === 0 || !input) {
    return null;
  }

  if (typeof input === 'number') {
    if (!Number.isFinite(input)) {
      return null;
    }

    const date = new Date(toUnixMilliseconds(input));

    return isKnownDate(date) ? date : null;
  }

  if (DATE_ONLY_PATTERN.test(input)) {
    const parsed = parse(input, 'yyyy-MM-dd', new Date());

    return isKnownDate(parsed) ? parsed : null;
  }

  const parsed = parseISO(input);

  return isKnownDate(parsed) ? parsed : null;
}

function formatMonthDayYearWithoutTimezone(
  input: number | string,
  parsed: Date,
  monthFormat: MonthFormat,
): string {
  const pattern = MONTH_FORMAT_PATTERNS[monthFormat];

  if (typeof input === 'string' && !DATE_ONLY_PATTERN.test(input)) {
    return formatKnownDate(new TZDate(parsed.getTime(), 'UTC'), pattern);
  }

  return formatKnownDate(parsed, pattern);
}

export const formatMonthDayYear = (input: number | string, options: DateOptions = {}): string => {
  const { monthFormat = 'short', timezone } = options;
  const parsed = parseFlexibleDateInput(input);

  if (!parsed) {
    return UNKNOWN_DATE;
  }

  if (timezone) {
    try {
      const zonedDate = new TZDate(parsed.getTime(), timezone);

      if (isKnownDate(zonedDate)) {
        return formatKnownDate(zonedDate, MONTH_FORMAT_PATTERNS[monthFormat]);
      }
    } catch {
      // fall through to local formatting
    }

    return formatMonthDayYearWithoutTimezone(input, parsed, monthFormat);
  }

  return formatMonthDayYearWithoutTimezone(input, parsed, monthFormat);
};

export const scanDateToUnixTimestamp = (scanDate: string, timeZone?: null | string): number => {
  const dateOnly = scanDate.includes('T') ? scanDate.split('T')[0] : scanDate;
  const [year, month, day] = dateOnly.split('-').map(Number);

  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
    return 0;
  }

  return Math.floor(new TZDate(year, month - 1, day, timeZone ?? undefined).getTime() / 1000);
};

export const formatTimestampToDate = (timestamp: number, timezone?: null | string): string => {
  if (!isKnownTimestamp(timestamp)) {
    return UNKNOWN_DATE;
  }

  const unixMs = timestamp * 1000;

  if (timezone) {
    try {
      const zonedDate = new TZDate(unixMs, timezone);

      if (isKnownDate(zonedDate)) {
        return formatKnownDate(zonedDate, 'yyyy-MM-dd');
      }
    } catch {
      // fall through to local formatting
    }
  }

  return formatKnownDate(new Date(unixMs), 'yyyy-MM-dd');
};

export const formatTimeStampToUTC = (dateInput: Date | string): string => {
  const parsed = typeof dateInput === 'string' ? parseISO(dateInput) : dateInput;

  if (!isKnownDate(parsed)) {
    return UNKNOWN_DATE;
  }

  return formatKnownDate(new TZDate(parsed, 'UTC'), UTC_TIMESTAMP_FORMAT);
};
